import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import { useCallback } from "react";
import { z } from "zod";
import {
  equipmentResponseSchema,
  type EquipmentResponse,
} from "../../components/shared/EquipmentDropdown.types";
import { DaylightDateFormat } from "../../constants/DaylightDateFormat";
import { toastMessage } from "../../constants/strings";
import {
  deliveryEquipmentSchema,
  DeliveryEquipmentUsage,
  deliveryEquipmentUsageSchema,
} from "../../types/deliveryEquipment.type";
import {
  Equipment,
  equipmentSchema,
  equipmentsSchema,
  equipmentTypeIds,
  equipmentTypeMap,
  EquipmentTypeName,
} from "../../types/equipment.type";
import { http } from "../../utils/httpCommon";
import { toast } from "../../utils/snackbarHelper";
import { useApiUrls } from "../useApiUrl";

const deliveryEquipmentUsageSchemaByType = z.object({
  trailers: z.array(
    deliveryEquipmentUsageSchema.transform((data) => ({
      ...data,
      name: data.name || `Trailer # ${data.id}`,
    }))
  ),
  straightTrucks: z.array(
    deliveryEquipmentUsageSchema.transform((data) => ({
      ...data,
      name: data.name || `Straight Truck # ${data.id}`,
    }))
  ),
  tractors: z.array(
    deliveryEquipmentUsageSchema.transform((data) => ({
      ...data,
      name: data.name || `Tractor # ${data.id}`,
    }))
  ),
});

type DeliveryRouteEquipmentUsageSet = z.infer<
  typeof deliveryEquipmentUsageSchemaByType
>;

const deliveryEquipmentSchemaByType = z.object({
  trailers: z.array(
    deliveryEquipmentSchema.transform((data) => ({
      ...data,
      name: data.name || `Trailer # ${data.id}`,
    }))
  ),
  straightTrucks: z.array(
    deliveryEquipmentSchema.transform((data) => ({
      ...data,
      name: data.name || `Straight Truck # ${data.id}`,
    }))
  ),
  tractors: z.array(
    deliveryEquipmentSchema.transform((data) => ({
      ...data,
      name: data.name || `Tractor # ${data.id}`,
    }))
  ),
});

type DeliveryRouteEquipmentSet = z.infer<typeof deliveryEquipmentSchemaByType>;

const keys = {
  getEquipments: ["equipments"],
  getEquipmentById: (id: number) => ["equipments", id],
  getEquipmentDetailsBySicId: (sicId: number, types: string[]) => [
    "equipments",
    "sic",
    sicId,
    types,
  ],
  getEquipmentBySicId: (sicId: number) => ["equipments", "sic", sicId],
  getEquipmentUsageBySicId: (sicId: number, planDate: number) => [
    "equipments",
    "sic",
    sicId,
    planDate,
  ],
};

export const useEquipmentDetailsBySicId = (
  sicId: number,
  types: EquipmentTypeName[],
  enabled = true
) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { getEquipmentDetailsBySicId } = urls;
  return useQuery({
    enabled,
    queryKey: keys.getEquipmentDetailsBySicId(sicId, types),
    queryFn: async () => {
      const { data } = await http.get(getEquipmentDetailsBySicId(sicId), {
        params: {
          equipmentType: types.join(","),
        },
      });
      return equipmentsSchema.parse(data);
    },
  });
};

const useEquipmentDetailsBySicIdGroupedByTypeSelect = (
  types: EquipmentTypeName[]
) =>
  useCallback(
    (data: Equipment[]) =>
      // Return data grouped by equipment type
      types.reduce(
        (
          acc: { [key in EquipmentTypeName]: Equipment[] },
          typeName: EquipmentTypeName
        ) => {
          acc[typeName] = data.filter(
            (equipment) => equipment.typeName === typeName
          );
          return acc;
        },
        {
          "Straight Truck": [],
          Tractor: [],
          Trailer: [],
        } satisfies { [key in EquipmentTypeName]: Equipment[] }
      ),
    [types]
  );

export const useEquipmentDetailsBySicIdGroupedByType = (
  sicId: number,
  types: EquipmentTypeName[],
  enabled = true
) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { getEquipmentDetailsBySicId } = urls;
  return useQuery({
    enabled,
    queryKey: keys.getEquipmentDetailsBySicId(sicId, types),
    queryFn: async () => {
      const { data } = await http.get(getEquipmentDetailsBySicId(sicId), {
        params: {
          equipmentType: types.join(","),
        },
      });
      return equipmentsSchema.parse(data);
    },
    select: useEquipmentDetailsBySicIdGroupedByTypeSelect(types),
  });
};

export const useEquipmentById = (id: number | null) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { getEquipmentById } = urls;
  return useQuery({
    queryKey: keys.getEquipmentById(id ?? 0),
    queryFn: async () => {
      if (!id) return null;
      const { data } = await http.get(getEquipmentById(id));
      return equipmentSchema.parse(data);
    },
    enabled: Boolean(id),
  });
};

export const useEquipmentBySicId = (sicId: number, enabled = true) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { getEquipmentsBySicIdAndTypeId } = urls;
  return useQuery({
    enabled,
    queryKey: keys.getEquipmentBySicId(sicId),
    queryFn: async () => {
      // Current GetEquipment API doesn't allow us to pass params with an array of equipment types to make one call.
      // For now, we loop through all equipment types, call the API to get the list individually. Once the API is fixed,
      // we will make one call to get all the equipment list.
      const equipmentPromises = equipmentTypeIds.map((type) =>
        http.get(getEquipmentsBySicIdAndTypeId(sicId, type))
      );

      const [tractors, trailers, trucks] = await Promise.all(equipmentPromises);

      const equipments: DeliveryRouteEquipmentSet = {
        tractors: tractors.data,
        trailers: trailers.data,
        straightTrucks: trucks.data,
      };
      return deliveryEquipmentSchemaByType.parse(equipments);
    },
  });
};

export const useEquipmentUsageBySicId = (
  sicId: number,
  planDate: number,
  enabled = true
) => {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { getEquipmentsUsageBySicId } = urls;
  return useQuery({
    enabled,
    queryKey: keys.getEquipmentUsageBySicId(sicId, planDate),
    queryFn: async (): Promise<DeliveryRouteEquipmentUsageSet> => {
      const queryString = equipmentTypeIds
        .map((type) => `types=${type}`)
        .join("&");
      const { data } = await http.get<DeliveryEquipmentUsage[]>(
        `${getEquipmentsUsageBySicId(sicId)}?planDate=${planDate}&${queryString}`
      );

      if (data.length === 0) {
        return {
          tractors: [],
          trailers: [],
          straightTrucks: [],
        };
      }

      // TODO: Remove FE grouping by type once the BE returns it with that structure https://dylt.atlassian.net/browse/IODD-2964
      const tractors = data.filter(
        (e) => e.typeId === equipmentTypeMap.tractor
      );
      const trailers = data.filter(
        (e) => e.typeId === equipmentTypeMap.trailer
      );
      const straightTrucks = data.filter(
        (e) => e.typeId === equipmentTypeMap.straightTruck
      );

      return deliveryEquipmentUsageSchemaByType.parse({
        tractors,
        trailers,
        straightTrucks,
      });
    },
  });
};

const equipmentBySicKeys = {
  bySic: (sicId: number, types: number[], planDate?: number) =>
    ["equipment", "by-sic", sicId, types, planDate] as const,
};

type UseGetEquipmentBySicArgs = {
  sicId: number;
  types: number[];
  planDate?: number;
  enabled?: boolean;
};

export function useGetEquipmentBySic({
  sicId,
  types,
  planDate,
  enabled = true,
}: UseGetEquipmentBySicArgs) {
  const urls = useApiUrls();

  return useQuery({
    queryKey: equipmentBySicKeys.bySic(sicId, types, planDate),
    queryFn: async (): Promise<EquipmentResponse> => {
      const { data } = await http.get(urls.getEquipmentBySic(sicId), {
        params: {
          types,
          planDate: planDate
            ? dayjs(planDate.toString(), "YYYYMMDD").format(
                DaylightDateFormat.ISO_DATE
              )
            : undefined,
        },
      });
      return equipmentResponseSchema.parse(data);
    },
    enabled: enabled && sicId > 0 && types.length > 0,
  });
}

export function useDeleteEquipment() {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { deleteEquipment } = urls;

  return useMutation({
    mutationFn: async (equipmentId: number) => {
      const url = deleteEquipment(equipmentId);
      const response = await http.delete(url);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.getEquipments });
    },
  });
}

const equipmentKeys = {
  getEquipment: (equipmentId: number) => ["equipments", equipmentId],
  getEquipments: () => ["equipments"],
};

export function useUpdateEquipment() {
  const queryClient = useQueryClient();
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { putEquipment } = urls;

  return useMutation({
    mutationFn: async (equipment: Equipment) => {
      const url = putEquipment(equipment.id);
      await http.put<Equipment>(url, equipment);
      return { equipment };
    },
    onSuccess: ({ equipment }) => {
      queryClient.invalidateQueries({
        queryKey: equipmentKeys.getEquipments(),
      });
      queryClient.setQueryData(
        equipmentKeys.getEquipment(equipment.id),
        equipment
      );
      toast(toastMessage.settings.equipmentUpdated.success, {
        variant: "success",
      });
    },
    onError: () => {
      toast(toastMessage.settings.equipmentUpdated.failed, {
        variant: "error",
      });
    },
  });
}

export function useCreateEquipment() {
  const hookUrls = useApiUrls();
  const urls = hookUrls;
  const { postEquipments } = urls;

  return useMutation({
    mutationFn: async (equipment: Equipment) => {
      const url = postEquipments;
      const { data } = await http.post<Equipment[]>(url, [equipment]);
      return data;
    },
    onError: (e) => {
      if (!axios.isAxiosError(e)) {
        return toast(toastMessage.generics.error, { variant: "error" });
      }

      if (e.response?.data.includes("REF_TRAILER_NUMBER_ALREADY_EXISTS")) {
        return toast(
          toastMessage.settings.equipments.create.warning
            .duplicatedTrailerNumber,
          { variant: "warning" }
        );
      }
    },
  });
}
